/**
 * Toggle User Media Like UseCase
 * 
 * Handles liking/unliking video content following Flutter's pattern:
 * 1. Optimistically update SecureStorage (for instant UI feedback)
 * 2. Call Cloud Function to update Firestore + create notification
 * 3. Revert SecureStorage if Cloud Function fails
 * 
 * Pattern from Flutter: ContentReactionsRepository.likeContent
 * Source: lib/data/repository/content_reactions_repository.dart
 */

import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { cloudFunctionsService } from '@/data/services/cloudFunctionsService';
import { eventBus, APP_EVENT_KEYS } from '@/infrastructure/eventBus';
import { UserMediaLikeRemapper } from '@/domain/remappers/userMediaLike/userMediaLikeRemapper';
import { AppNotificationType } from '@/data/enums/notification/appNotificationType';
import type { UserMediaLikeStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { NotificationInfoDto, UsersInfoDto, MediaInfoDto } from '@/data/dtos/info/infoDtos';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';

export interface ToggleUserMediaLikeResult {
  ok: boolean;
  liked: boolean;
}

export class ToggleUserMediaLikeUseCase {
  private storage: SecureStorageWebCrypto;

  constructor(storage?: SecureStorageWebCrypto) {
    this.storage = storage || SecureStorageWebCrypto.getInstance();
  }

  /**
   * Toggle like state for a video
   *
   * @param media - The media to like/unlike
   * @param currentUserId - Current user's Firebase ID
   * @param currentUsername - Current user's name
   * @returns ok: cloud function succeeded; liked: post-toggle state (reverted on failure)
   */
  async execute(
    media: UserMediaEntity,
    currentUserId: string,
    currentUsername: string
  ): Promise<ToggleUserMediaLikeResult> {
    // Get current like state from storage
    const storageKey = SECURE_STORAGE_KEYS.LIKES + media.relativePath;
    const currentLike = await this.storage.getEncrypted<UserMediaLikeStorage>(storageKey);

    // Determine new like state
    const isCurrentlyLiked = currentLike?.liked ?? false;
    const newLikedState = !isCurrentlyLiked;

    // Create new like object
    const newLike: UserMediaLikeStorage = {
      mediaId: media.id,
      userId: currentUserId,
      peerUserId: media.firebaseId || '',
      path: media.relativePath,
      liked: newLikedState,
      createdDate: currentLike?.createdDate || Date.now(),
      updatedDate: Date.now(),
      username: currentUsername,
    };

    // Step 1: Optimistically update local storage for instant UI feedback
    await this.storage.setEncrypted(storageKey, newLike);

    // Optimistically update the likes count in all media lists
    const optimisticLikes = newLikedState
      ? (media.likes ?? 0) + 1
      : Math.max(0, (media.likes ?? 0) - 1);
    eventBus.emit(APP_EVENT_KEYS.CONTENT_ITEM_LIKES_UPDATED, { mediaId: media.id, likes: optimisticLikes });

    // Step 2: Prepare notification data (matching Flutter's AppNotification structure)
    const usersInfoDto: UsersInfoDto = {
      userId: currentUserId,
      name: currentUsername,
      peerUserId: media.firebaseId || '',
    };

    const mediaInfoDto: MediaInfoDto = {
      thumbnail: media.relativeThumbnailPath || '',
      path: media.relativePath,
      duration: media.duration || 0,
      firebaseId: media.id,
      aspectRatio: media.aspectRatio,
      type: media.type,
      allowUnlock: false,
    };

    const notificationDto: NotificationInfoDto = {
      usersInfo: usersInfoDto,
      timestamp: Date.now(),
      isUndo: !newLikedState,
      notificationType: AppNotificationType.contentLike,
      mediaInfo: mediaInfoDto,
      read: false,
    };

    // Step 3: Convert storage entity to DTO for Cloud Function
    const likeDto = UserMediaLikeRemapper.toDto(newLike);

    // Step 4: Call Cloud Function (handles Firestore + notification)
    try {
      const success = await cloudFunctionsService.toggleUserMediaLike(likeDto, notificationDto);

      if (!success) {
        throw new Error('Cloud Function returned success: false');
      }
    } catch (cloudFunctionError) {
      // Step 5: Revert storage and likes count on failure
      if (currentLike) {
        // Restore previous state
        await this.storage.setEncrypted(storageKey, currentLike);
      } else {
        // Remove the like if it didn't exist before
        await this.storage.remove(storageKey);
      }

      eventBus.emit(APP_EVENT_KEYS.CONTENT_ITEM_LIKES_UPDATED, { mediaId: media.id, likes: media.likes ?? 0 });

      return { ok: false, liked: isCurrentlyLiked };
    }

    return { ok: true, liked: newLikedState };
  }
}

// Export singleton instance
export const toggleUserMediaLikeUseCase = new ToggleUserMediaLikeUseCase();
